<template>
  <div v-if="haveBooking">
    <div class="dashboard-page-title">
      <div class="container-fluid">
        <h1>Edit Booking</h1>
      </div>
    </div>

    <div class="container-fluid">
      <booking-form
        ref="form"
        :property-mutator="setBookingProperty"
        :booking="booking"
        :validation-triggered="validationTriggered"
      >
      </booking-form>

      <div class="dashboard-block">
        <ladda-button
          :click="handleSaveBookingCallback"
          class="btn btn-primary">
          Save Booking
        </ladda-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { omit } from 'lodash'
import { helpers } from 'mixins/bookings'
import BookingForm from './BookingForm'
import { notify, alert } from 'utils'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  props: ['id'],

  data () {
    return {
      validationTriggered: false,
      saveBooking: true
    }
  },

  mixins: [helpers, OpportunityTracker],

  components: {
    BookingForm
  },

  computed: {
    ...mapState('booking', ['booking']),
    ...mapState('booking/request', ['formStatus']),
    ...mapGetters('booking', ['haveBooking'])
  },

  created () {
    this.getBooking({ id: this.id })
      .catch((error) => {
        this.handleCaughtHttpExceptionForBooking(error, 'The booking could not be found.')
      })
  },

  methods: {
    ...mapActions('booking', ['getBooking', 'updateBooking', 'setBookingProperty']),
    ...mapActions('booking/request', ['setFormStatus', 'getFormStatus']),

    validateAll () {
      this.validationTriggered = true
      if (this.formStatus) {
        return Promise.resolve()
      } else {
        return Promise.reject(new Error('The form has validation errors'))
      }
    },

    handleSaveBookingCallback () {
      if (this.booking.is_offer) {
        this.validateAll().then(() => {
          this.updateBooking({
            id: this.id,
            payload: omit(this.booking, ['client', 'opportunity', 'payments', 'talent', 'id'])
          })
            .then(() => {
              this.trackEvent(this.analytics.V2.JobEditEvent)
              this.setFormStatus(false)
              this.$router.push({name: 'bookings.show', params: {id: this.booking.id}})
            })
            .catch((error) => {
              this.$refs.form.injectServerErrors(error)
            })
        }).catch(() => {
          notify.error('There were form validation issues, please fix them and try again.')
          return Promise.resolve()
        })
      } else {
        const confirmAlertOptions = {
          'semi-critical': {
            icon: 'warning',
            dangerMode: false,
            title: this.$options.filters.sentenceCase(this.$starLabelPlural) + ' will be notified of the changes made to this request',
            text: 'Do you wish to proceed?',
            buttons: {
              confirm: {
                text: 'Confirm',
                className: 'btn-primary',
                value: 'confirm'
              }
            }
          },
          critical: {
            icon: 'warning',
            dangerMode: false,
            title: this.$options.filters.sentenceCase(this.$starLabel) + ' will be notified of the changes',
            text: 'Do you wish to proceed?',
            buttons: {
              confirm: {
                text: 'Confirm',
                className: 'btn-primary',
                value: 'confirm'
              }
            }
          }
        }

        const alertPromise = new Promise((resolve, reject) => {
          if ((!this.booking.hasOwnProperty('needsConfirmation') || !this.booking.needsConfirmation.status) || !this.$userIsAdmin) {
            resolve()
          } else {
            return alert.confirm(confirmAlertOptions[this.booking.needsConfirmation.type])
              .then((result) => {
                this.saveBooking = (result === 'confirm')
              })
              .then((resolve))
              .catch(reject)
          }
        })

        return alertPromise
          .then(() => {
            if (this.saveBooking) {
              this.validateAll().then((response) => {
                this.updateBooking({
                  id: this.id,
                  payload: omit(this.booking, ['client', 'opportunity', 'payments', 'talent', 'id'])
                })
                  .then(() => {
                    this.trackEvent(this.analytics.V2.JobEditEvent)
                    this.setFormStatus(false)
                    this.$router.push({name: 'bookings.show', params: {id: this.booking.id}})
                  })
                  .catch((error) => {
                    this.$refs.form.injectServerErrors(error)
                  })
              }).catch(() => {
                notify.error('There were form validation issues, please fix them and try again.')
                return Promise.resolve()
              })
            }
          })
      }
    }
  }
}
</script>
